import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import CaseIdBadge from "../../../shared/components/CaseIdBadge";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import DeletionCaseReviewPreviewStrip from "../../../shared/components/deletionCase/DeletionCaseReviewPreviewStrip";
import DeletionCaseExpandedPanel from "../../../shared/components/deletionCase/DeletionCaseExpandedPanel";
import {
  fetchDeletionCases,
  fetchDeletionCaseStats,
  formatCaseError,
} from "../casesApi";
import DeletionCasesFilterBar from "../components/DeletionCasesFilterBar";
import DeletionCasesMetricsCards from "../components/DeletionCasesMetricsCards";
import DeleteDeletionCaseModal from "../components/DeleteDeletionCaseModal";
import { useTranslation, getCaseStatusLabel } from "../../../i18n";
import { formatCaseId } from "../casesApi";

function formatDateLocale(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(locale === "de" ? "de-DE" : "en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusDisplay(status, t) {
  switch (status) {
    case "initiated":
      return { label: getCaseStatusLabel(t, status), className: "bg-surface-container-high text-on-surface-variant" };
    case "started":
    case "legal_action":
      return { label: getCaseStatusLabel(t, status), className: "bg-secondary-container text-on-secondary-container" };
    case "completed":
      return { label: t("cases.statusResolved"), className: "bg-green-100 text-green-800" };
    case "rejected":
      return { label: getCaseStatusLabel(t, status), className: "bg-error-container text-on-error-container" };
    default:
      return { label: status, className: "bg-surface-container-high text-on-surface-variant" };
  }
}

function caseProgress(status) {
  switch (status) {
    case "initiated":
      return { width: "20%", barClass: "bg-secondary", trackClass: "bg-surface-container-high", resolved: false };
    case "started":
      return { width: "45%", barClass: "bg-secondary", trackClass: "bg-surface-container-high", resolved: false };
    case "legal_action":
      return { width: "65%", barClass: "bg-secondary", trackClass: "bg-surface-container-high", resolved: false };
    case "completed":
      return { width: "100%", barClass: "bg-green-600", trackClass: "bg-green-600", resolved: true };
    case "rejected":
      return { width: "100%", barClass: "bg-error", trackClass: "bg-error", resolved: true };
    default:
      return { width: "10%", barClass: "bg-secondary", trackClass: "bg-surface-container-high", resolved: false };
  }
}

const ClientDeletionCasesPage = () => {
  const { t, locale } = useTranslation();
  const location = useLocation();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusGroupFilter, setStatusGroupFilter] = useState("");
  const [createdWithinDaysFilter, setCreatedWithinDaysFilter] = useState(null);
  const [violationFilter, setViolationFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [caseToDelete, setCaseToDelete] = useState(null);

  useEffect(() => {
    const nav = location.state;
    if (!nav) return;
    if (nav.expandCaseId) {
      setExpandedId(nav.expandCaseId);
    }
    if (nav.caseSubmitted) {
      setSuccessMessage(nav.caseSubmitted);
    }
    if (nav.statusGroup) {
      setStatusGroupFilter(nav.statusGroup);
      setStatusFilter("");
      setCreatedWithinDaysFilter(null);
    } else if (nav.status) {
      setStatusFilter(nav.status);
      setStatusGroupFilter("");
      setCreatedWithinDaysFilter(null);
    } else if (nav.createdWithinDays) {
      setCreatedWithinDaysFilter(nav.createdWithinDays);
      setStatusFilter("");
      setStatusGroupFilter("");
    }
  }, [location.state]);

  const hasActiveFilters = Boolean(
    statusFilter ||
      statusGroupFilter ||
      createdWithinDaysFilter ||
      violationFilter ||
      search.trim()
  );

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await fetchDeletionCaseStats();
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDeletionCases({
        status: statusFilter || undefined,
        statusGroup: statusGroupFilter || undefined,
        createdWithinDays: createdWithinDaysFilter || undefined,
        violationType: violationFilter || undefined,
        search: search.trim() || undefined,
      });
      setCases(data);
    } catch (err) {
      setError(formatCaseError(err));
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, statusGroupFilter, createdWithinDaysFilter, violationFilter, search]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadCases();
    }, search ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [loadCases, search]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleCaseDeleted = (deletedCase) => {
    setCases((prev) => prev.filter((item) => item.id !== deletedCase.id));
    setExpandedId((prev) => (prev === deletedCase.id ? null : prev));
    setSuccessMessage(
      t("cases.deleteModal.success", { caseId: formatCaseId(deletedCase) })
    );
    loadStats();
  };

  return (
    <main className="flex min-h-screen flex-col bg-background font-body-md text-on-surface">
      <div className="space-y-10 p-margin-desktop">
        <div className="space-y-6">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <p className="max-w-2xl font-body-lg text-on-surface-variant">
              {t("cases.pageSubtitle")}
            </p>
            <Link
              to="/dashboard/client/reviews"
              className="flex items-center gap-2 rounded-lg bg-secondary px-6 py-3 font-semibold text-white shadow-lg shadow-secondary/20 transition-all hover:scale-[1.02] active:scale-95"
            >
              <MaterialIcon name="add_circle" />
              {t("cases.createNewCase")}
            </Link>
          </div>

          <DeletionCasesMetricsCards
            stats={stats}
            loading={statsLoading}
            activeStatus={statusFilter}
            activeStatusGroup={statusGroupFilter}
            activeCreatedWithinDays={createdWithinDaysFilter}
            onFilterChange={({ status, statusGroup, createdWithinDays }) => {
              setStatusFilter(status || "");
              setStatusGroupFilter(statusGroup || "");
              setCreatedWithinDaysFilter(createdWithinDays ?? null);
            }}
          />
        </div>

        <div className="space-y-6">
          {successMessage ? (
            <div className="rounded-xl border border-secondary/30 bg-secondary-container/30 px-4 py-3 text-body-md text-on-secondary-container">
              {successMessage}
            </div>
          ) : null}

          <DeletionCasesFilterBar
            variant="enterprise"
            statusFilter={statusFilter}
            statusGroupFilter={statusGroupFilter}
            onStatusFilterChange={(value) => {
              setStatusFilter(value);
              if (value) {
                setStatusGroupFilter("");
                setCreatedWithinDaysFilter(null);
              }
            }}
            violationFilter={violationFilter}
            onViolationFilterChange={setViolationFilter}
            search={search}
            onSearchChange={setSearch}
          />

          {error && (
            <p className="rounded-lg border border-error-container bg-error-container px-4 py-2 text-label-sm text-on-error-container">
              {error}
            </p>
          )}

          {loading ? (
            <div className="tonal-card flex flex-col items-center justify-center rounded-xl py-16">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-surface-container-high border-t-secondary"
                aria-hidden
              />
              <p className="mt-4 font-body-md font-medium text-on-surface-variant">
                {t("cases.loadingCases")}
              </p>
            </div>
          ) : cases.length === 0 ? (
            <div className="tonal-card rounded-xl px-6 py-14 text-center">
              <MaterialIcon
                name="folder_open"
                className="mx-auto mb-4 text-[48px] text-outline-variant"
              />
              <p className="font-headline-md text-headline-md text-on-surface">
                {hasActiveFilters ? t("cases.noCasesFilter") : t("cases.noCasesYet")}
              </p>
              <p className="mt-2 font-body-md text-on-surface-variant">
                {hasActiveFilters
                  ? t("cases.noCasesFilterHint")
                  : t("cases.noCasesYetHint")}
              </p>
              {!hasActiveFilters && (
                <Link
                  to="/dashboard/client/reviews"
                  className="mt-6 inline-flex items-center gap-2 rounded-lg bg-secondary px-6 py-3 font-semibold text-white hover:opacity-90"
                >
                  {t("cases.goToReviews")}
                  <MaterialIcon name="arrow_forward" className="text-base" />
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {cases.map((c) => {
                const expanded = expandedId === c.id;
                const reviewCount = c.items?.length ?? 0;
                const status = statusDisplay(c.status, t);
                const progress = caseProgress(c.status);
                const isResolved = c.status === "completed";

                return (
                  <article
                    key={c.id}
                    className={[
                      "tonal-card group overflow-hidden rounded-xl",
                      isResolved ? "opacity-80 transition-opacity hover:opacity-100" : "",
                    ].join(" ")}
                  >
                    <div className="p-6">
                      <div className="mb-4 flex items-start justify-between">
                        <div className="space-y-1">
                          <CaseIdBadge caseData={c} variant="enterprise" />
                          <h3 className="font-headline-md text-headline-md font-bold">
                            {c.violationType}
                          </h3>
                          <p className="flex items-center gap-2 font-body-md text-on-surface-variant">
                            <MaterialIcon name="calendar_month" className="text-[16px]" />
                            {formatDateLocale(c.createdAt, locale)}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-3">
                          <span
                            className={[
                              "rounded-full px-4 py-1 font-label-caps text-[11px] uppercase tracking-wider",
                              status.className,
                            ].join(" ")}
                          >
                            {status.label}
                          </span>
                          <div className="flex flex-wrap items-center justify-end gap-3">
                            {c.status === "initiated" ? (
                              <button
                                type="button"
                                onClick={() => setCaseToDelete(c)}
                                className="inline-flex items-center gap-1 font-label-sm text-error hover:text-error/80"
                              >
                                <MaterialIcon name="delete_outline" className="text-[18px]" />
                                {t("cases.withdrawCase")}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="flex items-center gap-1 font-label-sm text-on-surface-variant hover:text-primary"
                              onClick={() => toggleExpand(c.id)}
                              aria-expanded={expanded}
                            >
                              {t("cases.viewDetails")}
                              <MaterialIcon
                                name={expanded ? "expand_more" : "chevron_right"}
                                className="text-[18px]"
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      {reviewCount > 0 && !expanded && (
                        <div className="space-y-3">
                          <span className="font-label-caps text-[10px] uppercase tracking-widest text-on-surface-variant">
                            {t("cases.requestedRatings", { count: reviewCount })}
                          </span>
                          <DeletionCaseReviewPreviewStrip
                            items={c.items}
                            variant="enterprise"
                            onViewAll={() => toggleExpand(c.id)}
                          />
                        </div>
                      )}

                      {expanded && c.justification ? (
                        <div className="mt-4 rounded-lg border border-outline-variant/30 bg-surface-container-low p-4">
                          <span className="font-label-caps text-[10px] uppercase tracking-widest text-on-surface-variant">
                            {t("cases.submissionReason")}
                          </span>
                          <p className="mt-2 font-body-md text-on-surface">{c.justification}</p>
                        </div>
                      ) : null}
                    </div>

                    <div
                      className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                        expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="border-t border-outline-variant/30 bg-surface-container-low px-6 py-5">
                          <DeletionCaseExpandedPanel
                            justification={c.justification}
                            updatedAt={c.updatedAt}
                            items={c.items}
                            outcomeSummary={c.outcomeSummary}
                            showJustification={false}
                            showReviewOutcomes
                          />
                        </div>
                      </div>
                    </div>

                    <div className={`h-1 w-full overflow-hidden ${progress.trackClass}`}>
                      <div
                        className={`h-full transition-all duration-1000 ${progress.barClass}`}
                        style={{ width: progress.width }}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <footer className="mt-auto flex items-center justify-between border-t border-outline-variant bg-surface-container-lowest px-margin-desktop py-4">
        <span className="font-label-sm text-on-surface-variant">
          {t("cases.copyright")}
        </span>
        <div className="flex items-center gap-6">
          <Link
            to="/dashboard/client/documentation"
            className="font-label-sm text-on-surface-variant transition-colors hover:text-primary hover:underline hover:underline-offset-4"
          >
            {t("cases.privacyPolicy")}
          </Link>
          <Link
            to="/dashboard/client/documentation"
            className="font-label-sm text-on-surface-variant transition-colors hover:text-primary hover:underline hover:underline-offset-4"
          >
            {t("cases.termsOfService")}
          </Link>
          <Link
            to="/dashboard/client/documentation"
            className="font-label-sm text-on-surface-variant transition-colors hover:text-primary hover:underline hover:underline-offset-4"
          >
            {t("cases.compliance")}
          </Link>
        </div>
      </footer>

      <DeleteDeletionCaseModal
        open={Boolean(caseToDelete)}
        caseData={caseToDelete}
        onClose={() => setCaseToDelete(null)}
        onDeleted={handleCaseDeleted}
      />
    </main>
  );
};

export default ClientDeletionCasesPage;
